import { memo, useCallback } from "react";

import { FeatureIslandContainer, FeatureIslandMainWithBackGround } from "@/components/features/_layouts";
import type { SiteControllerSettingBeds24FormModel } from "@/components/features/settings/site-controller/model/beds24/type";
import type { SiteControllerSettingBeds24FormModelMethodsType } from "@/components/features/settings/site-controller/model/beds24/use-case/use-create-model/methods";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { Button } from "@/components/ui/button/button";

type Props = {
	beds24ViewModel: SiteControllerSettingBeds24FormModel["ViewModel"];
	siteControllerSettingBeds24FormModelMethods: SiteControllerSettingBeds24FormModelMethodsType;
};

export const SettingsSiteControllerBeds24ManualDataSyncIsland = ({
	beds24ViewModel,
	siteControllerSettingBeds24FormModelMethods: _siteControllerSettingBeds24FormModelMethods,
}: Props) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const { setUpRoomPriceByChillnn, beds24RoomTypeMapping } = beds24ViewModel;

	const shouldShow =
		setUpRoomPriceByChillnn?.value === false &&
		beds24RoomTypeMapping.value.some((item) => item.beds24roomID.value && item.roomID?.value);

	const { onSyncPriceAndStockData: _onSyncPriceAndStockData } =
		_siteControllerSettingBeds24FormModelMethods.siteControllerSettingBeds24FormModelMethods;

	const onSyncPriceAndStockData = useCallback(async () => {
		showLoadingScreenCallback();

		const { errors } = await _onSyncPriceAndStockData().finally(() => {
			hideLoadingScreenCallback();
		});

		if (!errors) {
			notifyUserEventInfo({
				title: "最新の情報に更新しました",
			});
		}
	}, [_onSyncPriceAndStockData, hideLoadingScreenCallback, showLoadingScreenCallback]);

	return (
		<>
			{shouldShow && (
				<FeatureIslandContainer>
					<FeatureIslandMainWithBackGround islandTitle="手動でのデータ連動">
						<SupplementalText />
						<div>
							<Button
								type="button"
								onClick={onSyncPriceAndStockData}
								variant={shouldShow ? "outline" : "disabled"}
							>
								最新の情報に更新する
							</Button>
						</div>
					</FeatureIslandMainWithBackGround>
				</FeatureIslandContainer>
			)}
		</>
	);
};

const SupplementalText = memo(() => {
	return (
		<div className="space-y-4">
			<div className="flex flex-wrap gap-x-2">
				<p>連携される設定には</p>

				<p className="font-semibold underline decoration-wavy underline-offset-4">自動で更新される設定</p>
				<p>と</p>
				<p className="font-semibold underline decoration-wavy underline-offset-4">
					手動で更新する必要がある設定
				</p>
				<p>の2種類があります。</p>
			</div>

			<div className="ml-4">
				<div className="mb-4 flex flex-col gap-2">
					<p className="font-semibold underline underline-offset-4">自動で更新される設定</p>
					<ul className="list-inside list-disc text-sm">
						<li>在庫数</li>
						<li>日別料金の基本料金</li>
						<li>価格乗数</li>
					</ul>
				</div>
				<div className="flex flex-col gap-2">
					<div>
						<p className="mb-2 font-semibold underline underline-offset-4">手動での更新が必要な設定</p>
						<p className="text-sm">※ 上記以外で料金に関わる設定を更新した場合、手動での更新が必要です。</p>
					</div>
					<div className="ml-6 flex flex-col gap-4">
						<div className="flex flex-col gap-2">
							<p className="font-semibold underline underline-offset-4">日別料金</p>
							<ul className="list-inside list-disc text-sm">
								<li>料金連携を解除する</li>
								<li>日別料金詳細設定 の 価格設定</li>
								<li>日別料金詳細設定 の 人数追加設定</li>
							</ul>
						</div>

						<div className="flex flex-col gap-2">
							<p className="font-semibold underline underline-offset-4">部屋のセットアップ（部屋設定）</p>
							<ul className="list-inside list-disc text-sm">
								<li>日別料金詳細設定で価格設定を「最大収容人数」に設定している</li>
								<li className="ml-6 list-[circle]">部屋のセットアップ の 最大宿泊人数</li>
							</ul>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
});

SettingsSiteControllerBeds24ManualDataSyncIsland.displayName = "SettingsSiteControllerBeds24ManualDataSyncIsland";
